import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useSpring, useTransform } from "framer-motion";
import { ArrowRight, TrendingDown } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/components/SEO";
import { calcEmi, inrFull } from "@/lib/format";
import { MoneyInput, MoneyWords } from "@/components/MoneyInput";

const AnimatedMoney = ({ value, className, testId }) => {
  const spring = useSpring(value, { stiffness: 110, damping: 24 });
  useEffect(() => {
    spring.set(value);
  }, [value, spring]);
  const text = useTransform(spring, (v) => inrFull(v));
  return <motion.span data-testid={testId} className={className}>{text}</motion.span>;
};

const FIXED_CHARGES = 10000;

export default function SavingsPage() {
  const [outstanding, setOutstanding] = useState(4000000);
  const [currentRate, setCurrentRate] = useState(9.4);
  const [months, setMonths] = useState(180);
  const [newRate, setNewRate] = useState(8.6);
  const [feePct, setFeePct] = useState(0.5);

  useSEO({
    title: "Balance Transfer Savings Calculator — See What Switching Saves | Loan Salaah",
    description: "Calculate exactly how much you save by moving your existing loan to a lower rate — monthly saving, interest saved, switching costs and break-even.",
  });

  const outstandingNum = Number(outstanding) || 0;

  const r = useMemo(() => {
    const P = outstandingNum;
    const n = months;
    const cur = calcEmi(P, currentRate, n);
    const nw = calcEmi(P, newRate, n);
    const monthlySave = cur.emi - nw.emi;
    const grossSave = (cur.emi - nw.emi) * n;
    const switchCost = (P * feePct) / 100 + FIXED_CHARGES;
    const net = grossSave - switchCost;
    const breakeven = monthlySave > 0 ? switchCost / monthlySave : Infinity;
    return { cur, nw, monthlySave, grossSave, switchCost, net, breakeven };
  }, [outstandingNum, currentRate, months, newRate, feePct]);

  const applyParams = `/apply?product=balance-transfer&amount=${outstandingNum}&rate=${newRate}&tenure=${months}&emi=${Math.round(r.nw.emi)}`;
  const worthIt = r.net > 0 && newRate < currentRate;

  return (
    <div data-testid="savings-page" className="pb-24 pt-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <div className="flex items-center gap-4">
            <span className="brass-rule" aria-hidden="true" />
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">Balance transfer</span>
          </div>
          <h1 className="mt-5 font-serif text-4xl font-semibold tracking-tight text-concordia sm:text-5xl lg:text-6xl">
            Loyalty is expensive. Do the maths.
          </h1>
          <p className="mt-5 text-base leading-relaxed text-slate">
            Enter your current loan details and a sharper rate. We show the monthly saving, the lifetime saving,
            the switching cost — and the honest net. No email required.
          </p>
        </Reveal>

        <Reveal delay={0.12} className="mt-12">
          <div className="grid overflow-hidden rounded-3xl border border-sand bg-white shadow-[0_30px_80px_rgba(30,74,61,0.1)] lg:grid-cols-5" data-testid="savings-calculator">
            {/* Controls */}
            <div className="space-y-10 p-6 sm:p-10 lg:col-span-3">
              <div>
                <div className="mb-3 flex items-end justify-between gap-4">
                  <label htmlFor="savings-outstanding-input" className="text-sm font-medium text-slate">Outstanding principal</label>
                  <MoneyInput
                    id="savings-outstanding-input"
                    testId="savings-outstanding-input"
                    wordsTestId="savings-outstanding-words"
                    value={outstanding}
                    max={100000000}
                    onChange={(v) => setOutstanding(v === "" ? "" : Math.min(100000000, v))}
                    onBlur={() => setOutstanding((a) => Math.min(100000000, Math.max(100000, Number(a) || 100000)))}
                  />
                </div>
                <Slider data-testid="savings-outstanding-slider" value={[Math.min(50000000, Math.max(100000, outstandingNum || 100000))]} min={100000} max={50000000} step={100000}
                  onValueChange={([v]) => setOutstanding(v)} className="slider-brand" />
                <div className="mt-2 flex justify-between font-mono text-[0.65rem] uppercase tracking-wider text-slate/70">
                  <span>₹1 L</span><span>₹5 Cr</span>
                </div>
              </div>

              <div className="grid gap-10 sm:grid-cols-2 sm:gap-6">
                <div>
                  <div className="mb-3 flex items-end justify-between gap-3">
                    <label htmlFor="savings-current-rate-input" className="text-sm font-medium text-slate">Current rate</label>
                    <div className="flex items-center rounded-xl border border-sand bg-cream px-3 py-1.5 focus-within:border-brass">
                      <input id="savings-current-rate-input" data-testid="savings-current-rate-input" type="number" step="0.05"
                        value={currentRate} min={6} max={24}
                        onChange={(e) => setCurrentRate(Math.min(24, Math.max(6, Number(e.target.value) || 6)))}
                        className="w-16 bg-transparent text-right font-semibold text-concordia outline-none" />
                      <span className="ml-1 font-semibold text-concordia">%</span>
                    </div>
                  </div>
                  <Slider data-testid="savings-current-rate-slider" value={[currentRate]} min={6} max={20} step={0.05}
                    onValueChange={([v]) => setCurrentRate(v)} className="slider-brand" />
                </div>
                <div>
                  <div className="mb-3 flex items-end justify-between gap-3">
                    <label htmlFor="savings-new-rate-input" className="text-sm font-medium text-slate">New rate offered</label>
                    <div className="flex items-center rounded-xl border border-sand bg-cream px-3 py-1.5 focus-within:border-brass">
                      <input id="savings-new-rate-input" data-testid="savings-new-rate-input" type="number" step="0.05"
                        value={newRate} min={6} max={24}
                        onChange={(e) => setNewRate(Math.min(24, Math.max(6, Number(e.target.value) || 6)))}
                        className="w-16 bg-transparent text-right font-semibold text-concordia outline-none" />
                      <span className="ml-1 font-semibold text-concordia">%</span>
                    </div>
                  </div>
                  <Slider data-testid="savings-new-rate-slider" value={[newRate]} min={6} max={20} step={0.05}
                    onValueChange={([v]) => setNewRate(v)} className="slider-brand" />
                </div>
              </div>

              <div className="grid gap-10 sm:grid-cols-2 sm:gap-6">
                <div>
                  <div className="mb-3 flex items-end justify-between gap-3">
                    <label htmlFor="savings-tenure-input" className="text-sm font-medium text-slate">Remaining tenure</label>
                    <div className="flex items-center rounded-xl border border-sand bg-cream px-3 py-1.5 focus-within:border-brass">
                      <input id="savings-tenure-input" data-testid="savings-tenure-input" type="number"
                        value={months} min={12} max={360}
                        onChange={(e) => setMonths(Math.min(360, Math.max(12, Math.round(Number(e.target.value) || 12))))}
                        className="w-16 bg-transparent text-right font-semibold text-concordia outline-none" />
                      <span className="ml-1 text-xs font-semibold text-slate">mo</span>
                    </div>
                  </div>
                  <Slider data-testid="savings-tenure-slider" value={[months]} min={12} max={360} step={6}
                    onValueChange={([v]) => setMonths(v)} className="slider-brand" />
                </div>
                <div>
                  <div className="mb-3 flex items-end justify-between gap-3">
                    <label htmlFor="savings-fee-input" className="text-sm font-medium text-slate">Switching charges</label>
                    <div className="flex items-center rounded-xl border border-sand bg-cream px-3 py-1.5 focus-within:border-brass">
                      <input id="savings-fee-input" data-testid="savings-fee-input" type="number" step="0.1"
                        value={feePct} min={0} max={3}
                        onChange={(e) => setFeePct(Math.min(3, Math.max(0, Number(e.target.value) || 0)))}
                        className="w-14 bg-transparent text-right font-semibold text-concordia outline-none" />
                      <span className="ml-1 font-semibold text-concordia">%</span>
                    </div>
                  </div>
                  <Slider data-testid="savings-fee-slider" value={[feePct]} min={0} max={2} step={0.1}
                    onValueChange={([v]) => setFeePct(v)} className="slider-brand" />
                  <p className="mt-2 text-[0.65rem] text-slate/70">+ {inrFull(FIXED_CHARGES)} estimated legal/valuation costs included</p>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="relative flex flex-col justify-between bg-concordia-ink p-6 text-ivory sm:p-10 lg:col-span-2">
              <div className="noise-overlay" aria-hidden="true" />
              <div className="relative">
                <p className="font-mono text-[0.62rem] uppercase tracking-[0.3em] text-brass">
                  {worthIt ? "Your net saving" : "Run the numbers"}
                </p>
                <AnimatedMoney value={Math.max(0, r.net)} testId="savings-net-value"
                  className="mt-3 block text-4xl font-bold tracking-tight text-ivory sm:text-5xl" />
                <MoneyWords value={Math.max(0, r.net)} dark testId="savings-net-words" />
                <p className="mt-2 text-xs text-ivory/50">over the remaining {months} months, after all switching costs</p>

                {newRate >= currentRate && (
                  <p className="mt-4 rounded-xl border border-brass/40 bg-brass/10 px-4 py-3 text-xs leading-relaxed text-ivory/80" data-testid="savings-rate-hint">
                    Your new rate isn't lower than the current one — a lower rate is where the saving comes from.
                  </p>
                )}

                <dl className="mt-8 space-y-4 text-sm">
                  <div className="flex items-center justify-between border-b border-ivory/10 pb-3">
                    <dt className="text-ivory/60">Current EMI</dt>
                    <dd className="text-right font-semibold" data-testid="savings-current-emi">{inrFull(r.cur.emi)}
                      <MoneyWords value={r.cur.emi} suffix="Rupees per month" dark /></dd>
                  </div>
                  <div className="flex items-center justify-between border-b border-ivory/10 pb-3">
                    <dt className="text-ivory/60">New EMI</dt>
                    <dd className="text-right font-semibold" data-testid="savings-new-emi">{inrFull(r.nw.emi)}
                      <MoneyWords value={r.nw.emi} suffix="Rupees per month" dark /></dd>
                  </div>
                  <div className="flex items-center justify-between border-b border-ivory/10 pb-3">
                    <dt className="text-ivory/60">Monthly saving</dt>
                    <dd className="text-right font-semibold text-sage" data-testid="savings-monthly">{inrFull(Math.max(0, r.monthlySave))}
                      <MoneyWords value={Math.max(0, r.monthlySave)} suffix="Rupees per month" dark /></dd>
                  </div>
                  <div className="flex items-center justify-between border-b border-ivory/10 pb-3">
                    <dt className="text-ivory/60">Switching cost (est.)</dt>
                    <dd className="text-right font-semibold" data-testid="savings-cost">{inrFull(r.switchCost)}
                      <MoneyWords value={r.switchCost} dark /></dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-ivory/60">Break-even</dt>
                    <dd className="font-semibold text-brass" data-testid="savings-breakeven">
                      {Number.isFinite(r.breakeven) ? `${Math.ceil(r.breakeven)} months` : "—"}
                    </dd>
                  </div>
                </dl>
              </div>

              <Link to={applyParams} data-testid="savings-apply-button"
                className="group relative mt-10 inline-flex items-center justify-center gap-2 rounded-full bg-ivory px-7 py-4 text-sm font-semibold text-concordia transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(0,0,0,0.35)]">
                <TrendingDown className="h-4 w-4" />
                Start my balance transfer
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </Reveal>

        <p className="mt-6 max-w-3xl text-xs leading-relaxed text-slate/80">
          Estimates for planning only. Actual savings depend on the new lender's sanctioned rate, fees, your repayment
          history and the remaining tenure. A Loan Salaah advisor computes your exact figure across lenders, free.
        </p>
      </div>
    </div>
  );
}
